// src/utils/storage.js
//
// Consent-aware cookie + localStorage helpers.
//
// Every write must declare which consent category it belongs to. Writes
// against a category the user has not consented to are silently dropped
// (returning false) so callers don't accidentally bypass the gate.
//
// Reads are NOT gated — once data exists, reading it is fine. We sweep
// disallowed data via purgeCategory() when consent changes.
//
// All browser-storage accesses (localStorage AND document.cookie) are
// wrapped to handle restricted contexts (sandboxed iframes, strict tracking
// protection, etc.) where the property access itself can throw a
// SecurityError. In those environments the util fails closed: writes are
// silently dropped, reads return empty/null.
//
// Usage:
//   import { setCookie, registerCookie, bindToStore } from '@/utils/storage';
//   bindToStore(store);  // once, in main.js after store is created
//   registerCookie('_ga', 'analytics');
//   setCookie('theme', 'dark', { category: 'functional', days: 365 });

let _store = null;

/**
 * Wire the storage util to the Vuex store. Must be called once after
 * the store is created. Subscribes to consent changes so disallowed
 * cookies / storage keys can be swept automatically.
 */
export function bindToStore(store) {
  _store = store;
  store.subscribeAction({
    after: (action) => {
      if (action.type === 'consent/purgeNonConsented') {
        purgeNonConsented();
      }
    },
  });
}

function canUse(category) {
  if (category === 'necessary') return true;
  if (!_store) {
    // Fail closed if the store isn't wired yet — better to drop than leak.
    return false;
  }
  return _store.getters['consent/canUse'](category) === true;
}

// ────────────────────────────────────────────────────────────────────
// Safe accessors
//
// Property access on window.localStorage and reads/writes on document.cookie
// can throw "The operation is insecure" / SecurityError in restricted
// contexts (sandboxed iframes, third-party storage blocked, strict tracking
// protection). Wrap them so callers always get a clean null/false fallback.
// ────────────────────────────────────────────────────────────────────
function getStorage() {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage || null;
  } catch (e) {
    return null;
  }
}

function readCookies() {
  try {
    return typeof document !== 'undefined' ? document.cookie : '';
  } catch (e) {
    return '';
  }
}

function writeCookie(str) {
  try {
    if (typeof document === 'undefined') return false;
    document.cookie = str;
    return true;
  } catch (e) {
    return false;
  }
}

// ────────────────────────────────────────────────────────────────────
// Registry: track which cookies / storage keys belong to which category
// so we can sweep them when consent is revoked or narrowed.
// ────────────────────────────────────────────────────────────────────
const cookieRegistry = {
  functional: new Set(),
  analytics: new Set(),
  advertising: new Set(),
};
const storageRegistry = {
  functional: new Set(),
  analytics: new Set(),
  advertising: new Set(),
};

export function registerCookie(name, category) {
  if (cookieRegistry[category]) cookieRegistry[category].add(name);
}
export function registerStorageKey(key, category) {
  if (storageRegistry[category]) storageRegistry[category].add(key);
}

// ────────────────────────────────────────────────────────────────────
// Cookies
// ────────────────────────────────────────────────────────────────────
export function setCookie(name, value, options = {}) {
  const {
    category = 'necessary',
    days,
    path = '/',
    domain,
    sameSite = 'Lax',
    secure = true,
  } = options;

  if (!canUse(category)) return false;

  const parts = [`${encodeURIComponent(name)}=${encodeURIComponent(value)}`];
  parts.push(`path=${path}`);
  if (domain)   parts.push(`domain=${domain}`);
  parts.push(`SameSite=${sameSite}`);
  if (secure)   parts.push('Secure');
  if (typeof days === 'number') {
    parts.push(`expires=${new Date(Date.now() + days * 86400000).toUTCString()}`);
  }

  const ok = writeCookie(parts.join('; '));
  if (ok) registerCookie(name, category);
  return ok;
}

export function getCookie(name) {
  const target = encodeURIComponent(name) + '=';
  const cookies = readCookies().split(';');
  for (let c of cookies) {
    c = c.trim();
    if (c.indexOf(target) === 0) {
      return decodeURIComponent(c.substring(target.length));
    }
  }
  return null;
}

export function removeCookie(name, options = {}) {
  const { path = '/', domain } = options;
  const parts = [
    `${encodeURIComponent(name)}=`,
    `path=${path}`,
    'expires=Thu, 01 Jan 1970 00:00:00 GMT',
  ];
  if (domain) parts.push(`domain=${domain}`);
  writeCookie(parts.join('; '));
}

// ────────────────────────────────────────────────────────────────────
// localStorage
// ────────────────────────────────────────────────────────────────────
export function setStorageItem(key, value, category = 'necessary') {
  if (!canUse(category)) return false;
  const ls = getStorage();
  if (!ls) return false;
  try {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    ls.setItem(key, serialized);
    registerStorageKey(key, category);
    return true;
  } catch (e) {
    return false;
  }
}

export function getStorageItem(key, { parse = false } = {}) {
  const ls = getStorage();
  if (!ls) return null;
  try {
    const v = ls.getItem(key);
    if (v === null) return null;
    if (!parse) return v;
    try { return JSON.parse(v); } catch (e) { return v; }
  } catch (e) {
    return null;
  }
}

export function removeStorageItem(key) {
  const ls = getStorage();
  if (!ls) return;
  try { ls.removeItem(key); } catch (e) { /* noop */ }
}

// ────────────────────────────────────────────────────────────────────
// Sweep — called by the store subscription when consent narrows.
// ────────────────────────────────────────────────────────────────────
export function purgeCategory(category) {
  if (cookieRegistry[category]) {
    cookieRegistry[category].forEach((name) => removeCookie(name));
  }
  if (storageRegistry[category]) {
    storageRegistry[category].forEach((key) => removeStorageItem(key));
  }
}

export function purgeNonConsented() {
  if (!_store) return;
  ['functional', 'analytics', 'advertising'].forEach((cat) => {
    if (!_store.getters['consent/canUse'](cat)) purgeCategory(cat);
  });
}